import { findConfig } from '@repo/money'
import { formatNumber } from './formatNumber'
import { displayMoney } from './displayAmount'

/**
 * The EXACT stored amount, for the one surface where the precise figure matters
 * more than a tidy one: the EXPANDED bet-history row's "Won Amount".
 *
 * Money is stored at the currency's full wire precision (5 decimals for ordinary
 * fiat) but rendered at 2, and `roundAmount` TRUNCATES rather than rounds — so a
 * payout of 40.71978 DOP shows on screen as "40.71" while the operator's wallet is
 * credited 40.71978. Operators read the two figures disagreeing as a money
 * discrepancy (SoftSwiss raised it for DOP and SYP, Aug 2026); it is really the
 * display hiding a tail the API carries. The multipliers are derived from each
 * market's probability and margin, so they are not clean decimals and a payout
 * genuinely lands on 5 decimals.
 *
 * 🚨 This is deliberately NOT a global precision change. The collapsed row, the
 * balance and every other surface keep `displayMoney` at 2dp — showing five
 * decimals everywhere would be wrong for money. Only the expanded row, which has
 * the space and is where someone reconciling against the API looks, grows.
 *
 * Coins go through the SAME path, and originally did not: they were handed back to
 * `displayMoney` on the belief that a coin already rendered its own precision. It
 * does not. `getSmartDecimals` widens only BELOW 0.01, so every coin amount at or
 * above a hundredth collapsed to 2dp and a 0.1632 DASH win read "0.16" while the
 * /win payload carried 0.1632 — SoftSwiss raised it for DASH in Sept 2026, the
 * same complaint as DOP/SYP. A coin's wire exponent and its display precision are
 * equal in @repo/money (DASH 8/8, DOT 10/10), so the fiat rule already fits it.
 *
 * Trailing zeros beyond 2dp are trimmed, so a clean 40.70 stays "40.70" and only
 * genuinely-fractional amounts grow.
 */
export const displayExactMoney = (
  amount: string | number,
  currency: string,
  format = true
): string => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  if (!Number.isFinite(numericAmount)) return displayMoney(amount, format)

  let fullDecimals: number
  try {
    const config = findConfig(currency, currency)
    // `precision - shift` is the wire exponent, matching @repo/money's own
    // fromInteger/toInteger. It is NOT displayPrecision (2 for fiat, the thing we
    // are deliberately exceeding) and NOT precision alone, which would render a
    // kilo unit (kIDR, shift -3) three decimals short of what it actually carries.
    // For coins the two coincide (DASH 8/8, DOT 10/10), so one path serves both.
    fullDecimals = config.precision - config.shift
    // A double carries ~15 significant digits. Asking toFixed for more turns binary
    // dust into rendered decimals — BCT is precision 18, and (0.1).toFixed(18) is
    // "0.100000000000000006". Cap so the tail below is real, never dust.
    const intDigits =
      Math.abs(numericAmount) >= 1 ? Math.floor(Math.log10(Math.abs(numericAmount))) + 1 : 1
    fullDecimals = Math.min(fullDecimals, Math.max(2, 15 - intDigits))
  } catch {
    // Unknown currency: no precision to trust, so stay on the 2dp default rather
    // than inventing decimals.
    return displayMoney(amount, format)
  }

  if (fullDecimals <= 2) return displayMoney(amount, format)

  // Trim trailing zeros, but never below 2dp — a clean amount must not become
  // "40.7" or "40". toFixed rounds the final digit, which at the WIRE precision
  // can only clear float dust: the value cannot carry a digit beyond it.
  //
  // 🚨 Scanned rather than regexed on purpose. A named capture group here
  // ("(?<keep>...)") builds locally and under jest but FAILS the production build:
  // the widget targets es5 (@repo/typescript-config/nextjs.json) and tsc rejects
  // named groups below ES2018. That broke the v1.0.197 deploy.
  const fixed = Math.abs(numericAmount).toFixed(fullDecimals)
  const dot = fixed.indexOf('.')
  let end = fixed.length
  while (end > dot + 3 && fixed.charAt(end - 1) === '0') end--
  const decimals = dot === -1 ? 2 : end - dot - 1

  return format ? formatNumber(numericAmount, decimals) : numericAmount.toFixed(decimals)
}
